import { useEffect, useState } from "react";
import papi from "@/lib/partnerApi";
import { StatCard, SectionHead } from "./VdaShared";
import { Users, Copy, Phone, Globe, TrendingUp, Radar, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

export default function VdaDashboard({ goTo }) {
  const [d, setD] = useState(null);
  useEffect(() => { papi.get("/vda/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>;

  const bands = [
    { name: "High Priority", value: d.high, color: "#059669" },
    { name: "Medium Priority", value: d.medium, color: "#d97706" },
    { name: "Low Priority", value: d.low, color: "#94a3b8" },
  ];
  const contact = [
    { name: "With Phone", value: d.with_phone, color: "#0d9488" },
    { name: "With Website", value: d.with_website, color: "#0ea5e9" },
    { name: "With Email", value: d.with_email, color: "#8b5cf6" },
  ];

  return (
    <div>
      <SectionHead title="Dashboard" desc="Overview of all discovered vendor prospects."
        actions={<Button data-testid="dash-run-discovery" onClick={() => goTo("discover")} className="rounded-xl bg-teal-500 hover:bg-teal-600"><Radar className="mr-2 h-4 w-4" /> Run Discovery</Button>} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Unique Prospects" value={d.unique} icon={Users} accent="teal" />
        <StatCard label="Total Discovered" value={d.discovered} icon={TrendingUp} accent="sky" />
        <StatCard label="Duplicates Merged" value={d.duplicates} icon={Copy} accent="violet" />
        <StatCard label="Avg. Completeness" value={`${d.avg_completeness}%`} icon={TrendingUp} accent="emerald" />
        <StatCard label="With Phone" value={d.with_phone} sub={`${d.unique ? Math.round(d.with_phone / d.unique * 100) : 0}% of prospects`} icon={Phone} accent="teal" />
        <StatCard label="With Website" value={d.with_website} sub={`${d.unique ? Math.round(d.with_website / d.unique * 100) : 0}% of prospects`} icon={Globe} accent="sky" />
        <StatCard label="With Email" value={d.with_email} icon={Users} accent="violet" />
        <StatCard label="With Instagram" value={d.with_instagram} icon={Users} accent="rose" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-display text-base font-bold text-slate-900">Priority Distribution</h3>
          {d.unique === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={bands} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                {bands.map((b, i) => <Cell key={i} fill={b.color} />)}</Pie><Tooltip /><Legend /></PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-display text-base font-bold text-slate-900">Contactability</h3>
          {d.unique === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={contact} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} paddingAngle={2}>
                {contact.map((b, i) => <Cell key={i} fill={b.color} />)}</Pie><Tooltip /><Legend /></PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="font-display text-base font-bold text-slate-900">Recent Discovery Runs</h3>
        {(!d.runs || d.runs.length === 0) ? <Empty /> : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="pb-2">Category</th><th className="pb-2">City</th><th className="pb-2 text-right">Target</th><th className="pb-2 text-right">Discovered</th><th className="pb-2 text-right">Unique</th><th className="pb-2 text-right">Dupes</th><th className="pb-2">When</th></tr></thead>
              <tbody>
                {d.runs.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50">
                    <td className="py-2.5 font-medium text-slate-800">{r.category}</td>
                    <td className="py-2.5 text-slate-600">{r.city}</td>
                    <td className="py-2.5 text-right tabular-nums">{r.target}</td>
                    <td className="py-2.5 text-right tabular-nums">{r.discovered}</td>
                    <td className="py-2.5 text-right font-semibold tabular-nums text-teal-600">{r.unique}</td>
                    <td className="py-2.5 text-right tabular-nums text-slate-500">{r.duplicates}</td>
                    <td className="py-2.5 text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Empty() {
  return <div className="flex h-40 items-center justify-center text-sm text-slate-400">No data yet — run a discovery to get started.</div>;
}
